import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import moment from 'moment'

import routes from '../../routers'
import Layout from '../../components/Layout'
import { ROLES } from '../../utils/constants'
import { openCheckout } from '../../utils/chargebee'
import { apiError } from '../../api/account'
import { getGiftOptions, giftCheckout, getGifts, GIFT_STATUS_TEXT } from '../../api/gifts'

import '../PurchaseBook/styles.css'
import styles from './Gift.module.css'

const cap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '')
const dollars = (n) => (n === null || n === undefined ? '' : `$${Number(n).toFixed(2)}`)
const day = (d) => (d ? moment(d).format('MMM D, YYYY') : '')

const PLAN_TEXT = {
  pro: 'Unlimited recipes, photos and voice memos for one person, for a year.',
  family: 'Everything in Pro for them and up to 5 family members, for a year.',
}

// Choose a gift, who it's for, when it arrives and a message; then Chargebee's secure
// payment screen over the page
const GiftForm = ({ options }) => {
  const navigate = useNavigate()
  const [kind, setKind] = useState(options.after_cutoff ? 'cookbook' : 'pro')
  const [amount, setAmount] = useState(options.voucher_amounts[1] || options.voucher_amounts[0])
  const [form, setForm] = useState({
    recipient_name: '',
    recipient_email: '',
    deliver_on: options.today,
    message: '',
  })
  const [fields, setFields] = useState({})
  const [error, setError] = useState('')
  const [paying, setPaying] = useState(false)
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value })

  const price = useMemo(() => {
    if (kind === 'cookbook') return amount
    return options.plans.find((p) => p.kind === kind)?.price
  }, [kind, amount, options])

  const pay = (e) => {
    e.preventDefault()
    setError('')
    setFields({})
    setPaying(true)
    giftCheckout({ ...form, kind, amount: kind === 'cookbook' ? amount : undefined })
      .then(async ({ data }) => {
        // If the pop-up can't load, fall back to Chargebee's full payment page
        const result = await openCheckout(data.chargebee_site, data.hosted_page).catch(() => null)
        if (!result) {
          window.location.href = data.url
          return
        }
        if (result.paid) return navigate(`${routes.giftSent}/${data.gift_id}?paid=1`)
        setPaying(false)
      })
      .catch((err) => {
        setPaying(false)
        setFields(err?.response?.data?.fields || {})
        setError(apiError(err, 'Something went wrong. Please try again.'))
      })
  }

  const field = (key, label, input) => (
    <label className={styles.field}>
      {label}
      {input}
      {fields[key] ? <span className={styles.fieldError}>{fields[key]}</span> : null}
    </label>
  )

  return (
    <form className={styles.layout} onSubmit={pay}>
      <section className="cbSection">
        <h2 className="cbSectionTitle">Choose a gift</h2>
        <div className="cbChoices">
          {options.plans.map((p) => (
            <label key={p.kind} className={`cbChoice ${kind === p.kind ? 'cbChoiceOn' : ''}`}>
              <input
                type="radio"
                name="kind"
                checked={kind === p.kind}
                onChange={() => setKind(p.kind)}
              />
              <span className="cbChoiceDot" />
              <span className="cbChoiceBody">
                <b>A year of {p.name}</b>
                {PLAN_TEXT[p.kind]}
              </span>
              <span className={styles.price}>{dollars(p.price)}</span>
            </label>
          ))}
          <label className={`cbChoice ${kind === 'cookbook' ? 'cbChoiceOn' : ''}`}>
            <input
              type="radio"
              name="kind"
              checked={kind === 'cookbook'}
              onChange={() => setKind('cookbook')}
            />
            <span className="cbChoiceDot" />
            <span className="cbChoiceBody">
              <b>A cookbook voucher</b>
              Toward a printed cookbook of their family’s recipes, whenever they’re ready.
            </span>
          </label>
        </div>
        {kind === 'cookbook' ? (
          <div className={styles.amounts} role="radiogroup" aria-label="Voucher amount">
            {options.voucher_amounts.map((a) => (
              <button
                key={a}
                type="button"
                role="radio"
                aria-checked={amount === a}
                className={`${styles.amount} ${amount === a ? styles.amountOn : ''}`}
                onClick={() => setAmount(a)}
              >
                {dollars(a)}
              </button>
            ))}
            <p className="cbMuted">
              It pays for the book first, then shipping. Any amount left over isn’t kept.
            </p>
          </div>
        ) : null}
      </section>

      <section className="cbSection">
        <h2 className="cbSectionTitle">Who it’s for</h2>
        {field(
          'recipient_name',
          'Their name',
          <input
            className={`cbInput ${fields.recipient_name ? 'cbInputBad' : ''}`}
            value={form.recipient_name}
            onChange={set('recipient_name')}
            maxLength={100}
            required
          />
        )}
        {field(
          'recipient_email',
          'Their email',
          <input
            type="email"
            className={`cbInput ${fields.recipient_email ? 'cbInputBad' : ''}`}
            value={form.recipient_email}
            onChange={set('recipient_email')}
            maxLength={254}
            required
          />
        )}
        {field(
          'deliver_on',
          'Send the gift email on',
          <input
            type="date"
            className={`cbInput ${fields.deliver_on ? 'cbInputBad' : ''}`}
            value={form.deliver_on}
            min={options.today}
            max={options.max_deliver_on}
            onChange={set('deliver_on')}
            required
          />
        )}
        {field(
          'message',
          'Your message (optional)',
          <textarea
            className={`cbInput ${styles.message}`}
            value={form.message}
            onChange={set('message')}
            maxLength={500}
            rows={4}
            placeholder="Merry Christmas! Let’s finally get Grandma’s recipes in one place."
          />
        )}
        <div className={styles.summary}>
          <p>
            <span>Total before tax</span>
            <b>{dollars(price)}</b>
          </p>
          <p className="cbMuted">
            They get an email with a gift code on {day(form.deliver_on)}. Codes work for 12 months.
            If they already pay for Morsel, the gift adds a year, so nobody pays twice.
          </p>
        </div>
        {error ? <p className="cbError">{error}</p> : null}
        <button type="submit" className={`cbNextButton ${styles.pay}`} disabled={paying || !price}>
          {paying ? 'Opening secure payment…' : 'Continue to secure payment'}
        </button>
        <p className="cbMuted">You pay on Chargebee’s secure page; Morsel never sees your card.</p>
      </section>
    </form>
  )
}

const SentList = () => {
  const [gifts, setGifts] = useState(null)
  useEffect(() => {
    getGifts()
      .then(({ data }) => setGifts(data))
      .catch(() => setGifts([]))
  }, [])
  if (!gifts || !gifts.length) return null
  return (
    <section className="cbSection">
      <h2 className="cbSectionTitle">Gifts you’ve sent</h2>
      <div className={styles.list}>
        {gifts.map((g) => (
          <Link key={g.id} to={`${routes.giftSent}/${g.id}`} className={styles.row}>
            <span>
              <b>{g.recipient_name}</b>
              <small>
                {cap(g.what)},{' '}
                {g.status === 'paid' ? `arrives ${day(g.deliver_on)}` : day(g.paid_at)}
              </small>
            </span>
            <span className={styles.pill}>{GIFT_STATUS_TEXT[g.status] || g.status}</span>
          </Link>
        ))}
      </div>
    </section>
  )
}

const Gift = () => {
  const user = useSelector((state) => state.user?.data)
  const role = useSelector((state) => state.user?.userRole)
  const signedIn = role === ROLES.ROLE_USER
  const [options, setOptions] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    getGiftOptions()
      .then(({ data }) => setOptions(data))
      .catch(() => setError('Gifts are not available right now. Please try again soon.'))
  }, [])

  let body
  if (error) body = <p className="cbError">{error}</p>
  else if (!options) body = <p className="cbMuted">Loading…</p>
  else if (!signedIn) {
    body = (
      <section className="cbSection">
        <h2 className="cbSectionTitle">Sign in to send a gift</h2>
        <p className="cbHint">
          A year of Pro from {dollars(options.plans[0]?.price)}, Family Pro for up to 6 people, or a
          cookbook voucher. You need a Morsel account to send one; a free account is fine.
        </p>
        <div className={styles.buttons}>
          <Link className="cbNextButton" to={routes.signUp}>
            Create a free account
          </Link>
          <Link className="cbTextLink" to={routes.signIn}>
            Sign in
          </Link>
        </div>
      </section>
    )
  } else if (!user?.features?.gifts) {
    body = (
      <section className="cbSection">
        <h2 className="cbSectionTitle">Gifts are coming soon</h2>
        <p className="cbHint">
          You’ll be able to give Morsel Pro, Family Pro and cookbook vouchers here.
        </p>
      </section>
    )
  } else {
    body = (
      <>
        {options.after_cutoff ? (
          <p className={styles.cutoff}>
            It’s too late to print a book in time for Christmas, but a cookbook voucher arrives by
            email and they can order their book whenever they’re ready.
          </p>
        ) : null}
        <GiftForm options={options} />
        {user?.cookbook_enabled ? (
          <p className="cbMuted">
            Sending a printed cookbook?{' '}
            <Link className="cbTextLink" to={routes.purchaseBook}>
              Make the book
            </Link>
            , then tick “This is a gift” at checkout.
          </p>
        ) : null}
        <SentList />
      </>
    )
  }

  return (
    <Layout title="Give Morsel as a gift" allowGuests>
      <div className={`cbPage ${styles.page}`}>
        <div className="cbIntro">
          <div>
            <h1 className="cbTitle">Give Morsel as a gift</h1>
            <p className="cbSubtitle">
              Help someone keep their family’s recipes, photos and voices together.
            </p>
          </div>
        </div>
        {body}
      </div>
    </Layout>
  )
}

export default Gift
